import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import FetchData from '../fetch-api/Apifetch';
import { ALL_LIST_EMPLOYEE, LIST_REPORT, LIST_EMPLOYEEDEVICE, LIST_ADMINS_EMPLOYEEDEVICE } from '../utils/ApiRoute';
import { EncryptData } from '../utils/Constants';
import { EmpDropdowndash } from '../split/Keystroke/EmpDropdown';
import { FaChevronDown } from "react-icons/fa";

const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
    const [empList, setEmpList] = useState([]);
    const [deviceList, setDeviceList] = useState([]);
    const [empLoading, setEmpLoading] = useState(false);
    const [deviceLoading, setDeviceLoading] = useState(false);

    // Always read fresh from localStorage so stale closures do not bite us
    const getUser = () => {
        try { return JSON.parse(localStorage.getItem('token')); } catch { return null; }
    };

    const [user, setUser] = useState(getUser);

    const [selectedEmpId, setSelectedEmpId] = useState(() => {
        return localStorage.getItem('SearchUser') || user?.emp_id || null;
    });
    const [selectedDeviceId, setSelectedDeviceId] = useState(() => {
        return localStorage.getItem('dev_id') || '0';
    });

    // ─── Sync user state with localStorage ────────────────────────────────────
    useEffect(() => {
        const handleStorageChange = () => {
            const currentUser = getUser();
            setUser(currentUser);
            // Also sync IDs if they are missing but available in new user object
            if (currentUser) {
                const searchUser = localStorage.getItem('SearchUser');
                if (!searchUser) {
                    setSelectedEmpId(currentUser.emp_id);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // ─── Load Employees ───────────────────────────────────────────────────────
    const loadEmployees = useCallback(async () => {
        const currentUser = getUser();
        if (!currentUser) {
            console.warn('[GlobalContext] loadEmployees: no user in localStorage, skipping.');
            setEmpList([]);
            return;
        }

        setEmpLoading(true);
        try {
            const cryptoKey = JSON.parse(localStorage.getItem('crypto_key'));
            let endpoint;
            if (currentUser.type == 1) {
                endpoint = ALL_LIST_EMPLOYEE;
            } else {
                const encryptedId = EncryptData(currentUser.emp_id, cryptoKey);
                endpoint = LIST_REPORT + `?emp_id=${encodeURIComponent(encryptedId)}&type=${currentUser.type}`;
            }

            console.log('[GlobalContext] Fetching employees from:', endpoint);
            const res = await FetchData(endpoint, 'GET', null, true, false);
            console.log('[GlobalContext] Employee response:', res);

            let data = currentUser.type == 1 ? res?.data : res?.data?.reporting_employees;

            // Fix: Include the current employee in the list for regular users
            if (currentUser.type != 1 && res?.data?.employee) {
                data = data || [];
                // Check if the current employee is already in the list to prevent duplicates
                const isEmpInList = data.some(emp => emp.id === res.data.employee.id);
                if (!isEmpInList) {
                    data = [res.data.employee, ...data];
                }
            }

            if (Array.isArray(data) && data.length > 0) {
                setEmpList(data);
                console.log(`[GlobalContext] Loaded ${data.length} employees.`);
            } else {
                console.warn('[GlobalContext] Employee list is empty or invalid:', data);
                setEmpList([]);
            }
        } catch (err) {
            console.error('[GlobalContext] loadEmployees failed:', err?.message || err);
            setEmpList([]);
        } finally {
            setEmpLoading(false);
        }
    }, []);

    // ─── Load Devices ─────────────────────────────────────────────────────────
    const loadDevices = useCallback(async (empId, isEmpChange = false) => {
        const currentUser = getUser();
        if (!currentUser || !empId) {
            setDeviceList([]);
            return;
        }

        setDeviceLoading(true);
        try {
            const cryptoKey = JSON.parse(localStorage.getItem('crypto_key'));
            const encryptedId = EncryptData(empId, cryptoKey);
            const endpoint = currentUser.type == 1 ? LIST_ADMINS_EMPLOYEEDEVICE : LIST_EMPLOYEEDEVICE;

            console.log('[GlobalContext] Fetching devices for emp:', empId, 'from:', endpoint);
            const res = await FetchData(
                endpoint + `?emp_id=${encodeURIComponent(encryptedId)}`,
                'GET', null, true, false
            );
            console.log('[GlobalContext] Device response:', res);

            const devices = Array.isArray(res?.data) ? res.data : [];
            setDeviceList(devices);

            if (isEmpChange) {
                setSelectedDeviceId('0');
                localStorage.setItem('dev_id', '0');
            } else {
                const cachedDev = localStorage.getItem('dev_id');
                const isCachedValid = cachedDev && cachedDev !== '0' && devices.some(d => d.device_id === cachedDev);
                if (isCachedValid) {
                    setSelectedDeviceId(cachedDev);
                } else {
                    setSelectedDeviceId('0');
                    localStorage.setItem('dev_id', '0');
                }
            }
        } catch (err) {
            console.error('[GlobalContext] loadDevices failed:', err?.message || err);
            setDeviceList([]);
        } finally {
            setDeviceLoading(false);
        }
    }, []);

    // ─── React to User/Auth Changes ───────────────────────────────────────────
    useEffect(() => {
        if (user) {
            console.log('[GlobalContext] User detected/changed, triggering data load...');
            loadEmployees();

            const currentSelectedId = localStorage.getItem('SearchUser') || user.emp_id;
            setSelectedEmpId(currentSelectedId);

            if (currentSelectedId) {
                loadDevices(currentSelectedId, false);
            }
        } else {
            setEmpList([]);
            setDeviceList([]);
        }
    }, [user, loadEmployees, loadDevices]);

    // ─── Handlers ─────────────────────────────────────────────────────────────
    const handleGlobalUserChange = (empId) => {
        setSelectedEmpId(empId);
        localStorage.setItem('SearchUser', empId);
        setSelectedDeviceId('0');
        localStorage.setItem('dev_id', '0');
        loadDevices(empId, true);   // reset device for new employee
    };

    const handleGlobalDeviceChange = (devId) => {
        setSelectedDeviceId(devId);
        if (devId !== '0') {
            localStorage.setItem('dev_id', devId);
        }
    };

    return (
        <GlobalContext.Provider value={{
            empList,
            deviceList,
            empLoading,
            deviceLoading,
            selectedEmpId,
            selectedDeviceId,
            handleGlobalUserChange,
            handleGlobalDeviceChange,
            loadEmployees,
            user
        }}>
            {children}
        </GlobalContext.Provider>
    );
};

export const useGlobalContext = () => useContext(GlobalContext);

export const SelectedEmpContext = () => {
    const { empList, empLoading, selectedEmpId, handleGlobalUserChange } = useGlobalContext();
    const [localPlaceholder, setLocalPlaceholder] = useState('Search employee...');

    // When empList is loaded or selectedEmpId changes, re-evaluate placeholder
    useEffect(() => {
        if (empLoading) {
            setLocalPlaceholder('Loading...');
        } else if (empList && empList.length > 0) {
            const found = empList.find(item => item.emp_id === selectedEmpId);
            if (found) {
                setLocalPlaceholder(found.name);
            } else {
                setLocalPlaceholder('Search employee...');
                // If we have an empList but no selected emp found in the UI dropdown,
                // we optionally pre-select the first one so it doesn't stay blank
            }
        }
    }, [empList, empLoading, selectedEmpId]);

    return (
        <EmpDropdowndash
            placeholder={localPlaceholder}
            data={empList}
            handleChange={handleGlobalUserChange}
            selectedId={selectedEmpId}
        />
    );
};

export const SelectedDeviceContext = () => {
    const { deviceList, deviceLoading, selectedDeviceId, handleGlobalDeviceChange } = useGlobalContext();
    return (
        <div className="dropdown w-100">
            <button
                className="btn btn-drop-down dropdown-toggle w-100 d-flex justify-content-between align-items-center"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
            >
                <span>
                    {deviceLoading
                        ? 'Loading...'
                        : (selectedDeviceId !== '0' ? selectedDeviceId?.slice(0, 12) : 'All Devices')}
                </span>
            </button>
            <ul className="dropdown-menu w-100">
                <li>
                    <a className="dropdown-item" onClick={() => handleGlobalDeviceChange('0')}>
                        All Devices
                    </a>
                </li>
                {Array.isArray(deviceList) && deviceList.map((option) => (
                    <li key={option.device_id}>
                        <a
                            className="dropdown-item"
                            href="#"
                            onClick={(e) => { e.preventDefault(); handleGlobalDeviceChange(option.device_id); }}
                        >
                            {option.device_id}
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
};
